import { axiosInstance } from "@/lib/axios";
import {
  throwReportedWebImpossibleState,
} from "@/observability/report-error";
import {
  createAccountApi,
  type AppleSignInPayload,
  type AuthResponse,
  type GoogleSignInPayload,
  type LoginResult,
} from "@membentry/shared-api";
import type { UserDetailDTO } from "@membentry/shared-types";
import type {
  CreateUserFormData,
  LoginFormData,
  RegisterFormData,
} from "@membentry/shared-validations";

export type { LoginResult, AuthResponse };

const accountApi = createAccountApi(axiosInstance);

export const login = async (
  data: LoginFormData,
): Promise<LoginResult<UserDetailDTO>> => {
  return accountApi.login(data);
};

export const googleSignIn = async (
  data: GoogleSignInPayload,
): Promise<LoginResult<UserDetailDTO>> => {
  return accountApi.googleSignIn(data);
};

export const appleSignIn = async (
  data: AppleSignInPayload,
): Promise<LoginResult<UserDetailDTO>> => {
  return accountApi.appleSignIn(data);
};

export const linkSocialAccountByTicket = async (
  provider: "google" | "apple",
  linkTicket: string,
) => provider === "google"
  ? accountApi.linkGoogleAccountByTicket({ linkTicket })
  : accountApi.linkAppleAccountByTicket({ linkTicket });

export const verifyMfaCode = async (
  email: string,
  code: string,
): Promise<AuthResponse<UserDetailDTO>> => {
  return accountApi.verifyMfaLogin({ email, code });
};

export const setupMfa = async (): Promise<{
  qrCodeBase64: string;
  manualEntryKey: string;
  message: string;
}> => {
  return accountApi.setupMfa();
};

export const verifyMfaSetup = async (
  code: string,
): Promise<{ message: string; backupCodes: string[] }> => {
  return accountApi.verifyMfaSetup(code);
};

export const disableMfa = async (
  code: string,
): Promise<{ message: string }> => {
  return accountApi.disableMfa(code);
};

export const getCurrentUser = async (): Promise<UserDetailDTO> => {
  const response = await accountApi.getCurrentUser();

  if (!response.payload) {
    throwReportedWebImpossibleState({
      message: "Current user payload is missing",
      context: "Auth current-user response was missing its payload",
      tags: {
        seam: "api.auth.getCurrentUser",
      },
      extra: {
        hasPayload: false,
      },
      fingerprint: ["web", "auth", "current-user-missing-payload"],
    });
  }

  return response.payload;
};

export const register = async (data: RegisterFormData): Promise<void> => {
  await accountApi.register(data);
};

export const sendVerificationCode = async () => {
  return accountApi.sendVerificationCode();
};

export const verifyCode = async (code: string) => {
  return accountApi.verifyCode(code);
};

export const logout = async () => {
  await accountApi.logout();
};

export const createUser = async (
  data: CreateUserFormData,
): Promise<UserDetailDTO> => {
  return accountApi.createUser(data);
};
